import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Checkbox from "components/checkbox";
import Logo from "../../assets/img/auth/logo-chu-t.webp";
import {
  FiMail,
  FiLock,
  FiCalendar,
  FiUsers,
  FiActivity,
  FiClipboard,
  FiBell,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import AuthService from "../../service/auth.service";
import { Spinner } from "../../components/loaders/Spinner.js";
import InputWithIcon from "../../components/input/InputWithIcon";

export default function SignIn() {
  const [email_user, setEmailUser] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get("redirect");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate inputs before submission
    if (!email_user.trim() || !password.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await AuthService.login({ email_user, password });
      navigate(redirect || "/admin/default", { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      setError(
        error.response?.data?.message ||
          "Échec de la connexion. Veuillez vérifier vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  // Current date for display
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-lightPrimary to-white dark:from-navy-700 dark:to-navy-900">
      {/* Left Side - Branding & Features */}
      <div className="relative hidden w-1/2 overflow-hidden lg:flex lg:w-1/2">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-chuGreen-light to-chuGreen-dark">
          {/* Abstract pattern overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')",
            }}
          ></div>
        </div>

        <div className="relative z-10 flex w-full flex-col items-center justify-between py-12">
          {/* Logo and title */}
          <div className="mb-8 flex flex-col items-center">
            <div className="mb-4 h-28 w-28 overflow-hidden rounded-full">
              {/* Logo officiel CHU Tambohobe */}
              <img
                src={Logo}
                alt="Logo CHU Tambohobe"
                className="h-full w-full object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white">
              CHU TAMBOHOBE
            </h1>
            <p className="mt-2 text-green-100">
              Centre Hospitalier Universitaire
            </p>
          </div>

          {/* Feature highlights */}
          <div className="w-4/5 max-w-md">
            <div className="mb-6 rounded-xl border border-white/20 bg-white/10 p-6 text-white backdrop-blur-sm">
              <h3 className="flex items-center text-xl font-semibold">
                <FiUsers className="mr-2 text-chuPurple-light" /> Gestion des
                Services
              </h3>
              <p className="mt-2 text-sm text-green-100">
                Accédez à tous les outils de gestion hospitalière: patients,
                personnel, ressources médicales, et planification des services.
              </p>
            </div>

            <div className="rounded-xl border border-white/20 bg-white/10 p-6 text-white backdrop-blur-sm">
              <h3 className="flex items-center text-xl font-semibold">
                <FiCalendar className="mr-2 text-chuPurple-light" /> Actualités
                & Événements
              </h3>
              <p className="mt-2 text-sm text-green-100">
                Restez informé des dernières actualités, formations médicales et
                événements importants du centre hospitalier.
              </p>
            </div>
          </div>

          {/* Current date display */}
          {/* <div className="mt-8 rounded-full bg-chuGreen/30 px-4 py-2 text-green-100 backdrop-blur-sm">
            <p className="flex items-center">
              <FiCalendar className="mr-2" /> {currentDate}
            </p>
          </div> */}
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2">
        <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12 lg:px-8">
          {/* Mobile logo (visible only on small screens) */}
          <div className="mb-8 text-center lg:hidden">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-chuGreen to-chuGreen-dark">
              <img
                src={Logo}
                alt="Logo CHU Tambohobe"
                className="h-full w-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-chuGreen">CHU TAMBOHOBE</h1>
          </div>

          <div className="w-full max-w-md space-y-8 rounded-primary border-l border-t border-gray-100 bg-white p-8 dark:bg-navy-800">
            {/* Header */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Connectez-vous
                </h2>
                <div className="rounded-full bg-chuGreen-light/10 p-2 text-chuGreen">
                  <FiActivity className="h-5 w-5" />
                </div>
              </div>
              <p className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiBell className="mr-2 text-chuPurple" />
                Accédez à votre espace de travail médical
              </p>
            </div>

            {/* Form */}
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Email Input */}
                <div>
                  <label
                    htmlFor="email_user"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-400"
                  >
                    Adresse Email
                  </label>
                  <InputWithIcon
                    icon={FiMail}
                    placeholder="exemple@chu-tambohobe.mg"
                    id="email_user"
                    type="email"
                    value={email_user}
                    onChange={(e) => setEmailUser(e.target.value)}
                    disabled={loading}
                    required
                    className="rounded-xl focus:border-chuGreen focus:ring-chuGreen"
                  />
                </div>

                {/* Password Input */}
                <div>
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-400"
                  >
                    Mot de passe
                  </label>
                  <InputWithIcon
                    icon={FiLock}
                    placeholder="••••••••"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    className="rounded-xl focus:border-chuGreen focus:ring-chuGreen"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <label
                    className={`ml-2 text-sm ${
                      loading
                        ? "text-gray-400"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Se souvenir de moi
                  </label>
                </div>
                <div className="text-sm">
                  <Link
                    to="/"
                    className={`font-medium transition-colors ${
                      loading
                        ? "pointer-events-none text-chuPurple/50"
                        : "text-chuPurple hover:text-chuPurple-dark"
                    }`}
                    tabIndex={loading ? -1 : 0}
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="group relative flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-chuGreen-light to-chuGreen-dark px-4 py-3 text-sm font-semibold text-white shadow-md transition-all hover:from-chuGreen-dark hover:to-chuGreen-light focus:outline-none focus:ring-2 focus:ring-chuGreen focus:ring-offset-2 disabled:opacity-70"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Spinner size="sm" className="mr-2" />
                    <span>Connexion en cours...</span>
                  </div>
                ) : (
                  "Se connecter"
                )}
              </button>

              {/* Error Message */}
              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-3 text-center text-sm text-red-600">
                  {error}
                </div>
              )}
            </form>

            {/* Footer with info */}
            <div className="pt-2 text-center text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center justify-center space-x-2">
                <FiClipboard className="text-chuGreen" />
                <span>
                  Plateforme de gestion hospitalière — CHU Tambohobe © 2025
                </span>
              </div>
              {/* <div className="mt-2 text-chuPurple">
                {new Date().toLocaleDateString("fr-FR", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
